import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useLiveQuizSocket } from "../../contexts/LiveQuizSocketContext";
import { useTranslation } from "../../hooks/useTranslation";

const OPTIONS = [
  { bg: "#0a2a6e", icon: "▲", label: "A" },
  { bg: "#1368ce", icon: "◆", label: "B" },
  { bg: "#2fa8e0", icon: "●", label: "C" },
  { bg: "#0d7c8f", icon: "■", label: "D" },
];

const PHASES = { QUESTION: "question", RESULT: "result", LEADERBOARD: "leaderboard", FINISHED: "finished" };

export default function LiveQuizHostGame() {
  const { roomCode } = useParams();
  const { socket, setHostActiveRoom } = useLiveQuizSocket();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [phase, setPhase] = useState(PHASES.QUESTION);
  const [question, setQuestion] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [answeredBy, setAnsweredBy] = useState({ 0: [], 1: [], 2: [], 3: [] });
  const [voteCounts, setVoteCounts] = useState([0, 0, 0, 0]);
  const [correctIndex, setCorrectIndex] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [totalPlayers, setTotalPlayers] = useState(0);

  useEffect(() => {
    if (!socket) return;

    if (socket.connected) {
      socket.emit("join_room", { roomCode });
    } else {
      socket.once("connect", () => socket.emit("join_room", { roomCode }));
    }

    socket.on("player_answered", ({ playerName, optionIndex }) => {
      setAnsweredBy(prev => ({
        ...prev,
        [optionIndex]: [...(prev[optionIndex] || []), playerName],
      }));
    });

    socket.on("question_start", ({ index, total, question, options, timeLimit, timeLeft: tLeft }) => {
      setQuestion({ index, total, text: question, options, timeLimit });
      setTimeLeft(tLeft ?? timeLimit);
      setAnsweredBy({ 0: [], 1: [], 2: [], 3: [] });
      setVoteCounts([0, 0, 0, 0]);
      setCorrectIndex(null);
      setPhase(PHASES.QUESTION);
    });

    socket.on("question_result", ({ correctIndex, voteCounts }) => {
      setCorrectIndex(correctIndex);
      setVoteCounts(voteCounts);
      setPhase(PHASES.RESULT);
    });

    socket.on("leaderboard_update", ({ players }) => {
      setLeaderboard(players);
      setTotalPlayers(players.length);
      setPhase(PHASES.LEADERBOARD);
    });

    socket.on("quiz_finished", ({ leaderboard }) => {
      setLeaderboard(leaderboard);
      setPhase(PHASES.FINISHED);
      setHostActiveRoom(null);
    });

    socket.on("room_update", ({ count }) => {
      setTotalPlayers(count ?? 0);
    });

    return () => {
      socket.off("player_answered");
      socket.off("question_start");
      socket.off("question_result");
      socket.off("leaderboard_update");
      socket.off("quiz_finished");
      socket.off("room_update");
    };
  }, [socket]);

  useEffect(() => {
    if (phase !== PHASES.QUESTION || timeLeft <= 0) return;
    const timer = setInterval(() => setTimeLeft(p => p - 1), 1000);
    return () => clearInterval(timer);
  }, [phase, timeLeft]);

  const totalAnswered = Object.values(answeredBy).flat().length;
  const timerPercent = question ? (timeLeft / question.timeLimit) * 100 : 100;

  if (!socket) return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center"
      style={{ background: "#1a1a2e" }}>
      <div className="text-center text-white">
        <div className="spinner-border mb-3" />
        <p>{t.liveQuiz.connecting}</p>
      </div>
    </div>
  );

  if (phase === PHASES.QUESTION && question) return (
    <div className="min-vh-100 d-flex flex-column" style={{ background: "#1a1a2e" }}>
      <div className="p-3">
        <div className="progress" style={{ height: 10 }}>
          <div className="progress-bar bg-warning"
            style={{ width: `${timerPercent}%`, transition: "width 1s linear" }} />
        </div>
        <div className="d-flex justify-content-between text-white opacity-75 mt-1">
          <small>{t.liveQuiz.questionOf.replace("{{current}}", question.index + 1).replace("{{total}}", question.total)}</small>
          <small>⏱ {timeLeft}s — {t.liveQuiz.answered.replace("{{count}}", totalAnswered).replace("{{total}}", totalPlayers)}</small>
        </div>
      </div>

      <div className="flex-grow-1 d-flex align-items-center justify-content-center px-4">
        <h2 className="text-white text-center fw-bold">{question.text}</h2>
      </div>

      <div className="p-4 pb-5">
        <div className="row g-3">
          {question.options.map((opt, i) => (
            <div className="col-6" key={i}>
              <div className="rounded-3 p-3 h-100 d-flex flex-column justify-content-between"
                style={{ background: OPTIONS[i].bg }}>
                <div className="text-white fw-bold fs-5">
                  {OPTIONS[i].icon} {opt}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  if (phase === PHASES.RESULT && question) return (
    <div className="min-vh-100 d-flex flex-column align-items-center justify-content-center p-4"
      style={{ background: "#1a1a2e" }}>
      <h3 className="text-white fw-bold mb-4">{t.liveQuiz.result}</h3>
      <h4 className="text-white opacity-75 mb-4 text-center">{question.text}</h4>

      <div className="w-100" style={{ maxWidth: 600 }}>
        {question.options.map((opt, i) => (
          <div key={i} className="mb-3">
            <div className="d-flex align-items-center gap-2 mb-1">
              <span className="fw-bold text-white">{OPTIONS[i].icon} {opt}</span>
              {i === correctIndex && <span className="badge bg-success">✓ {t.liveQuiz.correct}</span>}
            </div>
            <div className="progress mb-1" style={{ height: 28 }}>
              <div className="progress-bar fw-bold"
                style={{
                  width: `${Math.max((voteCounts[i] / Math.max(...voteCounts, 1)) * 100, 5)}%`,
                  background: i === correctIndex ? "#26890c" : OPTIONS[i].bg,
                  transition: "width 0.6s ease",
                }}>
                {t.liveQuiz.votes.replace("{{count}}", voteCounts[i])}
              </div>
            </div>
            <div className="d-flex flex-wrap gap-1">
              {(answeredBy[i] || []).map((name, j) => (
                <span key={j} className="badge"
                  style={{ background: i === correctIndex ? "#26890c" : OPTIONS[i].bg, color: "#fff" }}>
                  {name}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  if (phase === PHASES.LEADERBOARD) return (
    <div className="min-vh-100 d-flex flex-column align-items-center justify-content-center p-4"
      style={{ background: "#1a1a2e" }}>
      <h3 className="text-white fw-bold mb-4">🏆 {t.liveQuiz.leaderboard}</h3>
      <div className="w-100" style={{ maxWidth: 500 }}>
        {leaderboard.map((p, i) => (
          <div key={i}
            className="d-flex justify-content-between align-items-center rounded-3 px-4 py-3 mb-2 text-white"
            style={{ background: i === 0 ? "#b8860b" : "rgba(255,255,255,0.1)" }}>
            <span className="fw-bold fs-5">
              {["🥇", "🥈", "🥉"][i] ?? `#${p.rank}`} {p.name}
            </span>
            <div className="text-end">
              <div className="fw-bold">{p.score} {t.liveQuiz.pts}</div>
              {p.lastDelta > 0 && <small className="text-success">+{p.lastDelta}</small>}
            </div>
          </div>
        ))}
      </div>
      <p className="text-white opacity-50 mt-4">{t.liveQuiz.nextQuestionSoon}</p>
    </div>
  );

  if (phase === PHASES.FINISHED) return (
    <div className="min-vh-100 d-flex flex-column align-items-center justify-content-center p-4"
      style={{ background: "#1a1a2e" }}>
      <h2 className="text-white fw-bold mb-1">🎉 {t.liveQuiz.quizFinished}</h2>
      <p className="text-white opacity-75 mb-4">{t.liveQuiz.finalResult}</p>
      <div className="w-100" style={{ maxWidth: 500 }}>
        {leaderboard.map((p, i) => (
          <div key={i}
            className="d-flex justify-content-between align-items-center rounded-3 px-4 py-3 mb-2 text-white"
            style={{ background: i === 0 ? "#b8860b" : "rgba(255,255,255,0.1)" }}>
            <span className="fw-bold fs-5">
              {["🥇", "🥈", "🥉"][i] ?? `#${p.rank}`} {p.name}
            </span>
            <span className="fw-bold fs-5">{p.score} {t.liveQuiz.points}</span>
          </div>
        ))}
      </div>
      <button className="btn btn-outline-light mt-4 px-5"
        onClick={() => navigate("/teacher/livequiz")}>
        {t.common.back}
      </button>
    </div>
  );

  return null;
}
